import React, { useEffect, useState } from "react";
import { apiCreateNewModel } from "api/apiAiModels";

import { App, Divider, Form } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import SelectedSectorsChart from "components/ModelCreation/SelectedSectorsChart";
import { apiCheckAiSymbol } from "api/apiAiResults";
import CreateModelForm from "./CreateModelForm";
import ModelCreationStatus from "./ModelCreationStatus";
import { modelTrainingChannel } from "utils/constants";

const ModelCreation = () => {
  const symbolsList = useSelector((state) => state.main.symbolsList);
  const symbolsDict = useSelector((state) => state.main.symbolsDict);
  const { message } = App.useApp();

  const [form] = Form.useForm();
  const [isPending, setIsPending] = useState(false);
  const [requestStatus, setRequestStatus] = useState("");
  const [isPredicting, setIsPredicting] = useState(false);
  const [modelData, setModelData] = useState(null);

  const convertIndicatorsNames = (indicators) => {
    const result = {};
    Object.entries(indicators).forEach(([indicatorName, indexedValues]) => {
      if (!indexedValues) return;
      Object.values(indexedValues).forEach((indicatorValue) => {
        if (indicatorValue === undefined || indicatorValue === null) return;
        let convertedName;
        if (typeof indicatorValue === "object") {
          const nestedValues = Object.values(indicatorValue).join("_");
          convertedName = `${indicatorName}_${nestedValues}`;
        } else {
          convertedName = `${indicatorName}_${indicatorValue}`;
        }
        result[convertedName] = indicatorValue;
      });
    });
    return result;
  };

  const handleCreateModel = async (values) => {
    const indicators = form.getFieldValue("indicators");
    if (!indicators || Object.keys(indicators).length === 0) {
      return;
    }
    const { leads, name, percToGain, days, modelType } = values;
    // hidden fields (dates in walk-forward mode) are read from the form store
    const dates = form.getFieldValue("dates");
    const testDates = values.walkForward ? null : form.getFieldValue("testDates");
    setIsPending(true);
    setRequestStatus("");
    try {
      const response = await apiCreateNewModel({
        leads,
        name,
        percToGain,
        days,
        indicators: convertIndicatorsNames(indicators),
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
        testStartDate: testDates?.[0]?.format("YYYY-MM-DD"),
        testEndDate: testDates?.[1]?.format("YYYY-MM-DD"),
        modelType,
        channel: modelTrainingChannel,
        resolution: values.resolution || "1d",
        evalOnly: values.evalOnly,
        n_estimators: values.n_estimators,
        max_depth: values.max_depth,
        walkForward: values.walkForward
          ? {
              train_years: values.wfTrainYears,
              first_test_year: values.wfTestYears[0].year(),
              last_test_year: values.wfTestYears[1].year(),
            }
          : undefined,
      });
      if (response.error) {
        setRequestStatus("error");
        message.error("Error creating model!");
        return;
      }
      setModelData(response);
      setRequestStatus(response._id);
      message.success("Model created successfully!");
    } catch (err) {
      setRequestStatus("error");
    }
    setIsPending(false);
  };

  // After a walk-forward run: same recipe (the form still holds it), trained like the next fold would be:
  // the `train_years` years before the current year, tested on the current year (kept out of the walk-forward)
  const prepareLiveModel = () => {
    const year = dayjs().year();
    const trainEnd = dayjs(`${year - 1}-12-31`);
    form.setFieldsValue({
      walkForward: false,
      evalOnly: false,
      name: `${form.getFieldValue("name")} (live)`,
      dates: [dayjs(`${year - modelData.results.walk_forward.train_years}-01-01`), trainEnd],
      testDates: [trainEnd.add(1, "day"), dayjs()],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    message.info(
      `Form set to train ${year - modelData.results.walk_forward.train_years}-${year - 1} and test ${year}. Check it and click Create new model.`
    );
  };

  const predictAllModelHistory = async () => {
    setIsPredicting(true);
    const values = form.getFieldsValue();
    const formData = {
      symbol: symbolsList.map((item) => item.ticker),
      modelId: requestStatus,
      startDate: dayjs("2000-01-01").format("YYYY-MM-DD"),
      endDate: dayjs().add(1, "d").format("YYYY-MM-DD"),
      percToGain: values.percToGain,
      days: values.days,
      indicators: convertIndicatorsNames(form.getFieldValue("indicators") || {}),
      channel: modelTrainingChannel,
      resolution: values.resolution || "1d",
    };
    try {
      await apiCheckAiSymbol(formData);
      setIsPredicting(false);
      setRequestStatus("success");
      message.success("Model Predicted successfully!");
      // form.resetFields();
    } catch (err) {}
  };

  useEffect(() => {
    document.title = "Create Model | Dindicator Dashboard";
  }, []);

  return (
    <>
      <Divider orientation="left">Create New Model</Divider>

      <div className="cards-wrapper">
        <CreateModelForm
          form={form}
          isPending={isPending}
          isPredicting={isPredicting}
          handleCreateModel={handleCreateModel}
          symbolsList={symbolsList}
        />
      </div>
      <div className="cards-wrapper">
        <ModelCreationStatus
          requestStatus={requestStatus}
          setRequestStatus={setRequestStatus}
          isPredicting={isPredicting}
          predictAllModelHistory={predictAllModelHistory}
          modelData={modelData}
          setModelData={setModelData}
          prepareLiveModel={prepareLiveModel}
        />
        <SelectedSectorsChart symbolsDict={symbolsDict} form={form} />
      </div>
    </>
  );
};

export default ModelCreation;
